/**
 * `localStorage` wrapper.
 *
 * Keys are namespaced with `meme-reply:` for a concrete reason: every GitHub Pages project site
 * on an account shares one origin, so two apps under `sgauruseu.github.io` see each other's
 * storage. An unprefixed key called `settings` would silently overwrite the neighbour's.
 *
 * Every access is guarded — `localStorage` throws in private browsing and when the quota is
 * full, and neither is a reason for the app to stop working.
 */

import type { Lang, Turn } from '../core/types.js';

const PREFIX = 'meme-reply:';
const SETTINGS_KEY = `${PREFIX}settings:v1`;
const HISTORY_KEY = `${PREFIX}history:v1`;

export const HISTORY_LIMIT = 30;

/** Fallback for when `localStorage` is unavailable, so the session still works. */
const memory = new Map<string, string>();

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return memory.get(key) ?? null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    memory.set(key, value);
  }
}

function readJson<T>(key: string, fallback: T): T {
  const raw = read(key);
  if (raw === null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export interface Settings {
  /** The language recognition and replies use. Must be set before the mic starts. */
  lang: Lang;
  /** Detect the language from typed text instead of trusting the switch. */
  autoDetect: boolean;
  /** Read replies out loud. */
  speakReplies: boolean;
  /** Allow calls to the live English joke APIs. Off means corpus only. */
  useLiveSources: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  lang: 'en',
  autoDetect: true,
  speakReplies: true,
  useLiveSources: true,
};

export function loadSettings(): Settings {
  const raw = readJson<Partial<Settings>>(SETTINGS_KEY, {});
  return {
    lang: raw.lang === 'ru' ? 'ru' : 'en',
    autoDetect: typeof raw.autoDetect === 'boolean' ? raw.autoDetect : true,
    speakReplies: typeof raw.speakReplies === 'boolean' ? raw.speakReplies : true,
    useLiveSources: typeof raw.useLiveSources === 'boolean' ? raw.useLiveSources : true,
  };
}

export function saveSettings(settings: Settings): void {
  write(SETTINGS_KEY, JSON.stringify(settings));
}

export function loadHistory(): Turn[] {
  const turns = readJson<Turn[]>(HISTORY_KEY, []);
  return Array.isArray(turns) ? turns.filter((t) => t?.reply?.text) : [];
}

export function saveHistory(turns: readonly Turn[]): void {
  write(HISTORY_KEY, JSON.stringify(turns.slice(0, HISTORY_LIMIT)));
}

/** Wipes everything this app has stored. Offered as a one-click reset. */
export function clearAll(): void {
  for (const key of [SETTINGS_KEY, HISTORY_KEY]) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    memory.delete(key);
  }
}
